import { motion } from 'framer-motion'
import type { Box } from '@/services/tour/tourModel'

interface TourRingProps {
  box: Box
  /** Changes on every step, so the tap plays once as each step lands. */
  stepKey: string
  reducedMotion: boolean
}

const SPRING = { type: 'spring', stiffness: 260, damping: 30 } as const

/**
 * The spotlight (spec §1): a 2 px ring in `foreground` at 60%, gliding from target to
 * target and pulsing slowly, with a dot that taps once as the step lands. Under reduced
 * motion it jumps, holds still and does not tap.
 */
export function TourRing({ box, stepKey, reducedMotion }: TourRingProps) {
  const at = { top: box.top, left: box.left, width: box.width, height: box.height }
  return (
    <>
      <motion.div
        aria-hidden="true"
        data-testid="tour-ring"
        data-pulse={reducedMotion ? 'off' : 'on'}
        className="pointer-events-none fixed rounded-lg border-2 border-foreground/60"
        initial={false}
        animate={reducedMotion ? { ...at, opacity: 1 } : { ...at, opacity: [0.6, 1, 0.6] }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : {
                top: SPRING,
                left: SPRING,
                width: SPRING,
                height: SPRING,
                opacity: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
              }
        }
      />
      {!reducedMotion && (
        <motion.div
          key={stepKey}
          aria-hidden="true"
          data-testid="tour-tap"
          className="pointer-events-none fixed h-3 w-3 rounded-full bg-foreground"
          style={{ top: box.top + box.height / 2 - 6, left: box.left + box.width / 2 - 6 }}
          initial={{ scale: 0, opacity: 0.8 }}
          animate={{ scale: [0, 1.4, 0], opacity: [0.8, 0.5, 0] }}
          transition={{ duration: 0.7, delay: 0.25, ease: 'easeOut' }}
        />
      )}
    </>
  )
}
